/**
 * Policies screen (v2 — Project Independence, DNKADM-0057 U2) — the cancellation/refund
 * bands table + Add/Edit dialog. Re-points the v1 screen onto `/v2` with the new
 * `scope` + `creditBucketId` model (handoff §5.12).
 *
 * Loads the bands through `useResource` (loading / ready / error + Retry) and renders
 * a table **sorted by `minHoursBeforeStart` descending** (matching the contract's
 * "largest qualifying" evaluation order). Columns: "Applies to" (the bucket name or
 * "All buckets", resolved from the 0052 bucket list) · "Cancel at least" (Xh before) ·
 * "Refund (%)" · row Edit/Delete.
 *
 * **Overlap warning (warn, never block).** Any band whose `(bucket,
 * minHoursBeforeStart)` scope appears more than once is flagged with a warning icon —
 * the server enforces uniqueness, but the UI only warns (a duplicate is a config smell,
 * and the "largest qualifying" rule still resolves it deterministically).
 *
 * **Code-default info note.** A standing note states that when NO band matches, the
 * system code-default applies (≥24h → 100%, else 0%) — that fallback is computed
 * server-side and is never a stored band.
 *
 * "Add band" and Edit open the {@link BandDialog} (the segmented "All buckets" + 0052
 * fail-closed bucket control, validated hours/percent). **Every write — create, update,
 * delete — refetches the list** (refetch-after-write); a `404` on delete just refreshes
 * the list (the band was already gone); any other upstream delete failure raises a
 * non-blocking error toast.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds an
 * "Applies to" search; every data column has a shared {@link SortableHeader}. The default
 * stays Cancel at least descending (see `policiesListView.ts`). Search and sort live in
 * the URL.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { matchesSearch } from '@/components/list/listSearch';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import {
  deleteCancellationBand,
  listCancellationBands,
  type CancellationBand,
} from '@/api/policies';
import { listCreditBuckets, type CreditBucket } from '@/api/creditBuckets';
import { bandKey, overlappingKeys } from './bandOverlap';
import { BandDialog } from './BandDialog';
import {
  BAND_DEFAULT_SORT,
  BAND_SORT_KEYS,
  POLICIES_URL_DEFAULTS,
  bandSortValue,
  type BandSortKey,
} from './policiesListView';
import { ScreenError } from '@/components/ScreenError';

/** Format a refund percent for display, trimming trailing zeros (e.g. 50, 33.5). */
function formatPercent(percent: number): string {
  // Round to two decimals (the stored precision), then drop trailing zeros.
  return `${Number(percent.toFixed(2))}`;
}

export function PoliciesScreen() {
  const showToast = useToast();
  // DNKADM-0430: search and sort come from the URL (`policiesListView.ts`).
  const url = useListUrlState(POLICIES_URL_DEFAULTS);
  const setUrl = url.set;
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, BAND_SORT_KEYS, BAND_DEFAULT_SORT);
  const bands = useResource(useCallback(() => listCancellationBands(), []));

  // Resolve bucket ids → names for the "Applies to" label. Load `all` so a band scoped
  // to a now-inactive bucket still resolves. A failure degrades to the raw id; it never
  // blocks the list.
  const buckets = useResource(useCallback(() => listCreditBuckets({ status: 'all' }), []));
  const bucketNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of (buckets.data ?? []) as CreditBucket[]) {
      map.set(b.id, b.name.length > 0 ? b.name : b.id);
    }
    return map;
  }, [buckets.data]);

  /** The bucket scope label for a row: the bucket name, or "All buckets" when `null`. */
  const appliesToLabel = useCallback(
    (creditBucketId: string | null): string =>
      creditBucketId === null
        ? 'All buckets'
        : bucketNameById.get(creditBucketId) ?? creditBucketId,
    [bucketNameById],
  );

  // Dialog state: `null` = closed; `{ band: null }` = add; `{ band }` = edit.
  const [dialog, setDialog] = useState<{ band: CancellationBand | null } | null>(null);

  const refetch = bands.refetch;

  // The rows in view: the search on the "Applies to" label, then the column sort (default
  // threshold DESCENDING, the "largest qualifying" order). The input is put in that
  // default order first (threshold descending, then label), so ties on any column keep
  // it (`sortRows` is stable). The overlap set flags any duplicated (bucket, hours) scope.
  const rows = useMemo(() => {
    const label = (band: CancellationBand) => appliesToLabel(band.creditBucketId);
    const inDefaultOrder = sortRows(
      (bands.data ?? []).filter((band) => matchesSearch(search, [label(band)])),
      (band) => band.minHoursBeforeStart,
      'desc',
      label,
    );
    return sortRows(inDefaultOrder, (band) => bandSortValue(band, sort.key, label(band)), sort.dir);
  }, [bands.data, appliesToLabel, search, sort.key, sort.dir]);
  const onSort = useCallback(
    (key: BandSortKey) => setUrl(toSortParams(toggleSort(sort, key), BAND_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const overlaps = useMemo(() => overlappingKeys(bands.data ?? []), [bands.data]);

  const handleDelete = useCallback(
    async (band: CancellationBand) => {
      if (band.id === null) return;
      try {
        await deleteCancellationBand(band.id);
      } catch (err) {
        // A 404 means the band was already gone — just refresh the list (no error).
        if (!(err instanceof ApiError && err.code === 'NOT_FOUND')) {
          const message =
            serverMessage(err) ?? 'The band couldn’t be deleted. Please try again.';
          showToast(message, 'error');
          return;
        }
      }
      refetch();
    },
    [refetch, showToast],
  );

  if (bands.status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        height={300}
        role="status"
        aria-label="Loading cancellation bands"
        sx={{ borderRadius: 3 }}
      />
    );
  }

  if (bands.status === 'error' || !bands.data) {
    return (
      <ScreenError
        error={bands.error}
        what="the cancellation bands"
        onRetry={bands.refetch}
        sx={{ alignItems: 'center' }}
      />
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2
        }}>
        <Typography variant="h6" component="h1">
          Cancellation refund bands
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ band: null })}
        >
          Add band
        </Button>
      </Stack>
      <Alert severity="info" icon={<Icon name="info" />} sx={{ mb: 2 }}>
        Each band sets how much of a booking’s credits are refunded based on how far ahead it
        is cancelled. A cancellation uses the band with the largest qualifying threshold. When
        no band matches, the built-in default applies: 100% if cancelled 24 hours or more
        ahead, otherwise 0%.
      </Alert>
      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search bands',
        }}
        onClear={() => setUrl({ q: null })}
      />
      {bands.data.length === 0 ? (
        <Alert severity="info" icon={<Icon name="gavel" />}>
          No cancellation bands yet. Add one to override the built-in default refund.
        </Alert>
      ) : (
        <TableContainer>
          <Table aria-label="Cancellation refund bands" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Applies to" col="appliesTo" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Cancel at least"
                  col="hours"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Refund (%)"
                  col="refund"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((band) => {
                const label = appliesToLabel(band.creditBucketId);
                const overlapping = overlaps.has(
                  bandKey(band.creditBucketId, band.minHoursBeforeStart),
                );
                return (
                  <TableRow key={band.id ?? `${label}-${band.minHoursBeforeStart}`}>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} sx={{
                        alignItems: "center"
                      }}>
                        <span>{label}</span>
                        {overlapping && (
                          <Tooltip title="Another band covers this bucket at the same hours.">
                            <Box component="span" sx={{ display: 'inline-flex', color: 'warning.main' }}>
                              <Icon name="warning" title="Overlapping band" sx={{ fontSize: 18 }} />
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{band.minHoursBeforeStart}h before</TableCell>
                    <TableCell align="right">{formatPercent(band.refundPercent)}</TableCell>
                    <TableCell align="right">
                      <IconAction
                        size="small"
                        label={`Edit band ${label} at ${band.minHoursBeforeStart} hours`}
                        onClick={() => setDialog({ band })}
                      >
                        <Icon name="edit" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Delete band ${label} at ${band.minHoursBeforeStart} hours`}
                        onClick={() => void handleDelete(band)}
                      >
                        <Icon name="delete" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {bands.data.length > 0 && rows.length === 0 && <ListNoMatch noun="bands" query={search} />}
      <BandDialog
        open={dialog !== null}
        band={dialog?.band ?? null}
        bands={bands.data}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
